import { Injectable, inject } from '@angular/core';
import { MapboxHelper } from './mapbox-map-helper';
import { MapLayerRenderer } from './mapbox-map-layer-renderer';

import * as mapboxgl from 'mapbox-gl';
import * as _ from 'lodash';
import moment from 'moment';
import { Layers } from './models';

@Injectable({
    providedIn: 'root'
})
export class MapLayerFactory {
    private _mapboxHelper = inject(MapboxHelper);

    private _mapLayerRenderers: { [key: string]: MapLayerRenderer } = {};


    constructor() {
    }

    public get allRenderedLayerNames(): string[] {
        let names = [];
        _.each(this._layerRenderes, renderer => names = names.concat(renderer.renderedLayerNames));
        return names;
    }

    // Används av lager av type GeoJSON
    public renderLayer(layerType: string, map: mapboxgl.Map, fc: GeoJSON.FeatureCollection) {
        const renderer = this._getRenderer(layerType);

        if (!renderer) {
            console.log(`could not find the right renderer. ${layerType}`);
            return;
        }

        renderer.updateData(map, fc);
        renderer.render(map);
    }

    // Används av lager där source är av type Vectorsource
    public renderLayerFromDataSet(layerType: string, map: mapboxgl.Map, sourceUrl: string, sourceLayer: string): string {
        const renderer = this._getRenderer(layerType);

        if (!renderer) {
            console.log(`could not find the right renderer. ${layerType}`);
            return;
        }

        const currentSourceUrl = this._mapboxHelper.getVectorSourceUrl(map, renderer.sourceId);
        if (currentSourceUrl === undefined) {
            // det finns ingen source, sker vid första renderingen
            renderer.setSourceLayer(sourceLayer);
            const promoteId = layerType == Layers.Lines ? 'shapeHash' : undefined;
            this._mapboxHelper.createVectorSource(map, sourceUrl, renderer.sourceId, promoteId);
            renderer.render(map);

        } else if (currentSourceUrl === sourceUrl) {
            // samma source URL, gör inget...

            const layer = map.getLayer(layerType);
            if (layer !== undefined && layer['source-layer'] !== sourceLayer) {
                renderer.setSourceLayer(sourceLayer);

                // ta bort lagret
                this._mapboxHelper.removeLayers(map, renderer.renderedLayerNames);
                renderer.render(map);
            }

        } else {
            // Source URL har ändrats, skapa nytt lager

            // ta bort lagret
            this._mapboxHelper.removeLayers(map, renderer.renderedLayerNames);

            // ta bort gamla sourcen
            this._mapboxHelper.removeSource(map, renderer.sourceId);

            // skapa ny source
            renderer.updateSourceLayer(sourceLayer);

            // skapa nytt lager med ny source
            const promoteId = layerType == Layers.Lines ? 'shapeHash' : undefined;
            this._mapboxHelper.createVectorSource(map, sourceUrl, renderer.sourceId, promoteId);
            renderer.render(map);
        }

        return renderer.sourceId;
    }

    public redrawLayers(map: mapboxgl.Map) {
        _.each(this._layerRenderes, renderer => renderer.render(map));
    }

    public resetRenderers() {
        _.each(this._layerRenderes, renderer => renderer.reset());
    }

    public getLayersByName(layerName: string) {
        let names = [];
        _.each(this._layerRenderes, renderer => names = names.concat(renderer.getAllLayerNames(layerName)));
        _.pull(names, undefined);
        return names;
    }

    public addRenderer(layerType: string, renderer: MapLayerRenderer) {
        const rendererExists = this._getRenderer(layerType) !== undefined;
        if (rendererExists) {
            return;
        }

        this._mapLayerRenderers[layerType] = renderer;
    }

    public clear() {
        this._mapLayerRenderers = {};
    }


    public getSourceObject(layerType: string): { source: string; sourceLayer?: string } {
        const renderer = this._getRenderer(layerType);
        const out: { source: string; sourceLayer?: string } = {
            source: renderer.sourceId
        };
        if (renderer.sourceLayer) {
            out.sourceLayer = renderer.sourceLayer;
        }
        return out;
    }

    private _getRenderer(layerType: string): MapLayerRenderer {
        return this._mapLayerRenderers[layerType];
    }

    private get _layerRenderes(): MapLayerRenderer[] {
        return Object.keys(this._mapLayerRenderers).map(key => this._mapLayerRenderers[key]);
    }
}
